import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import GenericPopup from "../../GenericPopup";
import PrimaryButton from "../../PrimaryButton";
import PrimaryOutlineButton from "../../PrimaryOutlineButton";
import Confetti from "../../Confetti/Confetti";
import MobileViews from "./MobileViews";
import InfoIcon from "../../../../Assets/Icons/info.svg";
import {
  approveOrRejectResources,
  createOffer,
  getNearestOffersByMerchant,
  getOffersByMerchant,
} from "../../../../services/offerService";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { getLocationById } from "../../../../services/franchiseService";
import toast from "../../../../utils/toast";
import { useOfferFormContext } from "../OfferFormContext";
import {
  getLiveOfferSaveFlags,
  getOfferEditProgressSaveFlags,
  resolveOfferLiveState,
} from "../offerApprovalHelpers";

const preferNonEmptyArray = (...sources) => {
  for (const source of sources) {
    if (Array.isArray(source) && source.length > 0) {
      return source;
    }
  }

  return Array.isArray(sources[sources.length - 1])
    ? sources[sources.length - 1]
    : [];
};

const OfferReview = ({
  handlePrevious,
  editOfferData,
  setPayloadData,
  selectedMerchant,
  handleSaveAndExit,
  payload,
  isApproved: isApprovedProp,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const offerId = useSelector(
    (state) => state?.offerReducer?.createdOffer?._id,
  );
  const role = useSelector(
    (state) => state?.authReducer?.tokenData?.user?.role,
  );
  const roleLower = (role || "").toLowerCase();
  const isAdminRole = roleLower === "admin";
  const isMerchantRole = roleLower === "merchant";
  const loading = useSelector((state) => state?.offerReducer?.loading);
  const createdOfferFromRedux = useSelector(
    (state) => state?.offerReducer?.createdOffer,
  );

  const [offerData, setOfferData] = useState(null);
  const [firstLocation, setFirstLocation] = useState(null);
  const [merchantData, setMerchantData] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showResubmitConfirm, setShowResubmitConfirm] = useState(false);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [isConfirmingResubmit, setIsConfirmingResubmit] = useState(false);
  const [successMessage, setSuccessMessage] = useState(
    "Your offer has been sent to admin. Once the offer has been live, you'll receive an email notification.",
  );
  // removed duplicates
  const [isFirstOffer, setIsFirstOffer] = useState(false);

  const { setNextLabel, registerNextAction, setIsNextLoading } =
    useOfferFormContext();

  const isLiveEdit = resolveOfferLiveState({
    isApprovedProp,
    editOfferData,
    offerData,
  });
  const liveEditPreview = useMemo(
    () => ({
      ...(editOfferData || offerData || {}),
      ...(offerData || {}),
      ...(payload || {}),
    }),
    [editOfferData, offerData, payload],
  );
  const progressSaveFlags = useMemo(
    () =>
      getOfferEditProgressSaveFlags({
        role,
        isEdit: Boolean(editOfferData),
        isApprovedProp,
        editOfferData,
        offerData,
      }),
    [role, isApprovedProp, editOfferData, offerData],
  );
  const getLocationData = async () => {
    const locId = offerData?.tempLocations?.[0]?._id;
    if (!locId) return;

    const res = await dispatch(
      getLocationById({ id: locId, silent: true }),
    )?.unwrap();
    if (res?.success) {
      setFirstLocation(res.data);
    }
  };

  const getMerchantData = async () => {
    const payload = {
      lat: firstLocation?.googlePlaceId?.lat,
      lng: firstLocation?.googlePlaceId?.lng,
      merchantId: offerData?.merchant?._id,
    };
    const res = await dispatch(
      getNearestOffersByMerchant({ ...payload, silent: true }),
    )?.unwrap();
    if (res?.success) {
      setMerchantData(res.data);
    }
  };

  const effectiveMerchantId =
    selectedMerchant?._id ||
    offerData?.merchant?._id ||
    offerData?.merchantId ||
    editOfferData?.merchant?._id ||
    editOfferData?.merchantId ||
    (typeof offerData?.merchant === "string" ? offerData.merchant : null) ||
    (typeof editOfferData?.merchant === "string"
      ? editOfferData.merchant
      : null) ||
    null;

  const currentOfferId =
    offerData?._id || editOfferData?._id || offerId || location.state?.id;
  const editStatus = String(
    editOfferData?.status || offerData?.status || "",
  ).toLowerCase();
  const isDeclinedEdit = ["declined", "disabled", "rejected"].includes(
    editStatus,
  );
  const isLive = resolveOfferLiveState({
    isApprovedProp,
    editOfferData,
    offerData,
  });
  const resolvedCategoryId =
    liveEditPreview?.category?._id ||
    liveEditPreview?.category?.id ||
    liveEditPreview?.category ||
    null;
  const resolvedSubCategoryIds = useMemo(
    () =>
      preferNonEmptyArray(
        liveEditPreview?.subCategory,
        editOfferData?.subCategory,
        offerData?.subCategory,
        [],
      ).map((s) =>
            typeof s === "object" ? s?._id || s?.id : s,
          ),
    [editOfferData?.subCategory, liveEditPreview?.subCategory, offerData?.subCategory],
  );
  const knownRuleObjectsById = useMemo(() => {
    const map = new Map();
    [
      ...(Array.isArray(editOfferData?.ruleAndRegulation)
        ? editOfferData.ruleAndRegulation
        : []),
      ...(Array.isArray(offerData?.ruleAndRegulation)
        ? offerData.ruleAndRegulation
        : []),
      ...(Array.isArray(liveEditPreview?.ruleAndRegulation)
        ? liveEditPreview.ruleAndRegulation
        : []),
    ].forEach((rule) => {
      if (rule && typeof rule === "object") {
        const id = rule?._id || rule?.id;
        if (id) {
          map.set(String(id), rule);
        }
      }
    });
    return map;
  }, [
    editOfferData?.ruleAndRegulation,
    liveEditPreview?.ruleAndRegulation,
    offerData?.ruleAndRegulation,
  ]);
  const resolvedRulesForComparison = useMemo(
    () =>
      preferNonEmptyArray(
        liveEditPreview?.ruleAndRegulation,
        editOfferData?.ruleAndRegulation,
        offerData?.ruleAndRegulation,
        [],
      )
        .map((rule) => {
          if (rule && typeof rule === "object") {
            return rule;
          }

          return knownRuleObjectsById.get(String(rule)) || rule;
        })
        .filter(Boolean),
    [
      editOfferData?.ruleAndRegulation,
      knownRuleObjectsById,
      liveEditPreview?.ruleAndRegulation,
      offerData?.ruleAndRegulation,
    ],
  );
  const resolvedRuleIds = useMemo(
    () =>
      resolvedRulesForComparison
        .map((rule) =>
          typeof rule === "object" ? rule?._id || rule?.id : rule,
        )
        .filter(Boolean),
    [resolvedRulesForComparison],
  );
  const resolvedLocationIds = useMemo(
    () =>
      preferNonEmptyArray(
        liveEditPreview?.tempLocations,
        liveEditPreview?.locations,
        editOfferData?.tempLocations,
        editOfferData?.locations,
        offerData?.tempLocations,
        offerData?.locations,
        [],
      )
        .map((location) =>
          typeof location === "object"
            ? location?._id || location?.id
            : location,
        )
        .filter(Boolean),
    [
      editOfferData?.locations,
      editOfferData?.tempLocations,
      liveEditPreview?.locations,
      liveEditPreview?.tempLocations,
      offerData?.locations,
      offerData?.tempLocations,
    ],
  );
  const comparisonPreview = useMemo(
    () => ({
      ...(editOfferData || offerData || {}),
      ...(offerData || {}),
      ...(payload || {}),
      category: resolvedCategoryId,
      subCategory: resolvedSubCategoryIds,
      ruleAndRegulation: resolvedRulesForComparison,
      tempLocations: resolvedLocationIds,
      locations: resolvedLocationIds,
      redemptionDays:
        liveEditPreview?.redemptionFrequency === "ONCE_IN_12_MONTHS"
          ? []
          : liveEditPreview?.redemptionDays,
    }),
    [
      editOfferData,
      liveEditPreview?.redemptionDays,
      liveEditPreview?.redemptionFrequency,
      offerData,
      payload,
      resolvedCategoryId,
      resolvedLocationIds,
      resolvedRulesForComparison,
      resolvedSubCategoryIds,
    ],
  );
  const liveSaveFlags = getLiveOfferSaveFlags({
    currentPayload: comparisonPreview,
    originalOffer: editOfferData || offerData,
    isLive: isLiveEdit,
    role,
    fallbackIsCreated: editOfferData?.isCreated ?? false,
    fallbackStatus: isLiveEdit ? "live" : null,
  });
  const shouldShowMerchantProcessingModal =
    isMerchantRole &&
    Boolean(editOfferData) &&
    ((isLiveEdit && liveSaveFlags.preserveLiveOffer) || editStatus === "pending");
  const shouldShowProcessingModal =
    (isAdminRole || shouldShowMerchantProcessingModal) &&
    isSubmittingReview &&
    !showSuccessModal;
  useEffect(() => {
    if (isAdminRole) {
      // Admin label: live offer → 'Save & Exit' (admin keeps it live)
      //              any other → 'Submit' (no resubmit concept for admin)
      setNextLabel(isLiveEdit ? "Save & Exit" : "Submit");
    } else {
      // Merchant label (existing logic)
      setNextLabel(
        isLiveEdit
          ? liveSaveFlags.preserveLiveOffer
            ? "Save & Exit"
            : "Resubmit"
          : editOfferData && editStatus !== "drafted"
            ? "Resubmit"
            : "Submit",
      );
    }
    return () => setNextLabel("Next");
  }, [
    isAdminRole,
    setNextLabel,
    editOfferData,
    editStatus,
    isLiveEdit,
    liveSaveFlags.preserveLiveOffer,
  ]);

  // Check if this is the merchant's first offer
  const checkIfFirstOffer = async () => {
    // Don't check for first offer if editing an existing offer
    if (editOfferData) {
      setIsFirstOffer(false);
      return;
    }

    // Get the merchant ID - for admin, use selectedMerchant; for merchant, use offerData merchant
    const merchantId = effectiveMerchantId;

    if (!merchantId) {
      setIsFirstOffer(false);
      return;
    }

    try {
      // Fetch merchant's offers to check if they have any completed offers
      const res = await dispatch(
        getOffersByMerchant({
          merchantId: merchantId,
          limit: 100, // Get a reasonable number to check
          page: 1,
          silent: true,
        }),
      )?.unwrap();

      if (res?.success) {
        // Filter for completed offers (isCreated: true) excluding the current offer
        const existingOffers = res?.data || [];
        const completedOffers = existingOffers.filter(
          (offer) => offer?.isCreated === true && offer?._id !== offerData?._id,
        );

        // This is the first offer if there are no existing completed offers
        setIsFirstOffer(completedOffers.length === 0);
      } else {
        setIsFirstOffer(false);
      }
    } catch (error) {
      // If error occurs, default to not showing celebration
      console.warn("Error checking if first offer:", error);
      setIsFirstOffer(false);
    }
  };

  useEffect(() => {
    const currentViewId = offerId || editOfferData?._id || location.state?.id;

    if (createdOfferFromRedux && createdOfferFromRedux._id === currentViewId) {
      setOfferData(createdOfferFromRedux);
      return;
    }

    if (editOfferData) {
      setOfferData(editOfferData);
    }
  }, [createdOfferFromRedux, editOfferData, offerId, location.state?.id]);

  useEffect(() => {
    if (offerData) {
      getLocationData();
    }
  }, [offerData]);

  useEffect(() => {
    if (offerData && firstLocation) {
      getMerchantData();
    }
  }, [firstLocation]);

  // Check if this is the first offer when offerData is available
  useEffect(() => {
    if (offerData && !editOfferData && role !== "admin") {
      checkIfFirstOffer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerData, editOfferData, role]);

  // Seed the review payload once without reacting to payload changes, or we would
  // overwrite the edited category/subcategory values on every render.
  useEffect(() => {
    if (!offerData) return;
    setPayloadData({
      merchantId: effectiveMerchantId,
      category: resolvedCategoryId,
      subCategory: resolvedSubCategoryIds,
      title: liveEditPreview?.title,
      offerType: liveEditPreview?.offerType,
      discountRange: liveEditPreview?.discountRange,
      moneySaved: liveEditPreview?.moneySaved,
      thumbnailImage: liveEditPreview?.thumbnailImage,
      previewImages: liveEditPreview?.previewImages,
      description: liveEditPreview?.description,
      startDate: liveEditPreview?.startDate,
      endDate: liveEditPreview?.endDate,
      redemptionDays:
        liveEditPreview?.redemptionFrequency === "ONCE_IN_12_MONTHS"
          ? undefined
          : liveEditPreview?.redemptionDays,
      ruleAndRegulation: resolvedRuleIds,
      locations: resolvedLocationIds,
      discountTitle: liveEditPreview?.discountTitle,
      id: currentOfferId,
      ...progressSaveFlags,
    });
  }, [
    currentOfferId,
    effectiveMerchantId,
    offerData?._id,
    editOfferData?._id,
    progressSaveFlags,
    resolvedLocationIds,
    resolvedRuleIds,
    setPayloadData,
  ]);

  const submitOffer = async ({
    closeResubmitConfirm = false,
    forceResubmit = false,
  } = {}) => {
    setIsSubmittingReview(true);
    const finalOfferCodes =
      createdOfferFromRedux?.offerCodes || offerData?.offerCodes;

    const resolvedOnlineOption =
      typeof offerData?.onlineOption === "string"
        ? offerData?.onlineOption
        : Array.isArray(finalOfferCodes) && finalOfferCodes.length > 0
          ? "ONLINE_WITH_CODE"
          : "ONLINE";

  const shouldMaintainLive =
    isMerchantRole && isLive && liveSaveFlags.preserveLiveOffer && !forceResubmit;
  const isDateRangeRule = liveEditPreview?.necessaryRule === "CHOOSE_DATE_RANGE";

    const payload = {
      merchantId: effectiveMerchantId,
      category: resolvedCategoryId,
      subCategory: resolvedSubCategoryIds,
      title: liveEditPreview?.title,
      offerType: liveEditPreview?.offerType,
      offerDiscountType: liveEditPreview?.offerDiscountType,
      offerCodes: finalOfferCodes,
      websiteURL: liveEditPreview?.websiteURL,
      ...(liveEditPreview?.offerType !== "INSTORE_SWOODLE_OFFER" &&
      liveEditPreview?.offerType !== "INSTORE_OFFER"
        ? { onlineOption: resolvedOnlineOption }
        : {}), // Only include onlineOption for non-instore offers
      necessaryRule: liveEditPreview?.necessaryRule,
      ...(!isDateRangeRule && liveEditPreview?.redemptionFrequency
        ? { redemptionFrequency: liveEditPreview?.redemptionFrequency }
        : {}),
      redemptionDays:
        liveEditPreview?.redemptionFrequency === "ONCE_IN_12_MONTHS"
          ? undefined
          : liveEditPreview?.redemptionDays,
      availableAcrossAllStores: liveEditPreview?.availableAcrossAllStores,
      discountRange: liveEditPreview?.discountRange,
      fixedDollarAmount: liveEditPreview?.fixedDollarAmount,
      thumbnailImage: liveEditPreview?.thumbnailImage,
      previewImages: liveEditPreview?.previewImages,
      ...(liveEditPreview?.description?.trim()
        ? { description: liveEditPreview?.description?.trim() }
        : {}),
      redemptionMethod: liveEditPreview?.redemptionMethod,
      startTime: liveEditPreview?.startTime,
      endTime: liveEditPreview?.endTime,
      callback_url: liveEditPreview?.callback_url,
      ruleAndRegulation: resolvedRuleIds,
      locations: resolvedLocationIds,
      discountTitle: liveEditPreview?.discountTitle,
      startDate: liveEditPreview?.startDate,
      endDate: liveEditPreview?.endDate,
      id: currentOfferId,
      // If merchant editing live with minor changes, keep it live.
      isCreated: true,
      ...(forceResubmit ? { forceResubmit: true } : {}),
      ...(shouldMaintainLive ? { status: "live" } : {}),
    };

    // Payload flags already set above

    if (
      !payload?.description ||
      `${payload?.description}`.trim().length === 0
    ) {
      delete payload.description;
    }

    try {
      const createRes = await dispatch(
        createOffer({
          ...payload,
          offerId: currentOfferId,
        }),
      )?.unwrap();

      if (!createRes?.success) return;

      if (isAdminRole) {
        // Fix 2: Admin submit status logic.
        // Previously always used status:'live', force-approving every admin
        // submission from the edit form. New rule:
        //   live offer  → stays live  (admin edited a live offer, keep it live)
        //   all others  → pending     (admin edited but did NOT approve it;
        //                              disabled→pending, drafted→pending,
        //                              pending→stays pending)
        const targetStatus = isLiveEdit ? "live" : "pending";
        const statusRes = await dispatch(
          approveOrRejectResources({
            type: "offer",
            status: targetStatus,
            ids: [offerData?._id],
            merchantId: selectedMerchant?._id,
            silent: true,
          }),
        )?.unwrap();
        if (statusRes?.success) {
          const adminSuccessMessage =
            targetStatus === "live"
              ? "Your offer changes are now live."
              : "Your offer has been submitted and is now pending review.";

          toast.success(adminSuccessMessage);
          setSuccessMessage(adminSuccessMessage);
          setShowSuccessModal(true);
        }
        return;
      }

      const returnedStatus = String(
        createRes?.data?.status || createRes?.offer?.status || "",
      ).toLowerCase();
      const shouldShowLiveSuccess =
        returnedStatus === "approved" ||
        returnedStatus === "live" ||
        (isMerchantRole && isLiveEdit && shouldMaintainLive);

      setSuccessMessage(
        shouldShowLiveSuccess
          ? "Your offer changes are now live."
          : "Your offer has been sent to admin. Once the offer has been live, you'll receive an email notification.",
      );

      if (closeResubmitConfirm) {
        setShowResubmitConfirm(false);
      }

      // Always show the success modal
      setShowSuccessModal(true);
      // Only show celebration (confetti) if this is the first offer
      if (isFirstOffer) {
        setShowCelebration(true);
      } else {
        setShowCelebration(false);
      }
      return true;
    } catch (error) {
      const message =
        error?.message ||
        error?.data?.message ||
        "We couldn't submit your offer just yet. Please review the required fields and try again.";
      toast.error(message);
      return false;
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const handleSubmit = async () => {
    const requiresMerchantResubmitConfirm =
      isMerchantRole &&
      !!editOfferData &&
      ((isLiveEdit && !liveSaveFlags.preserveLiveOffer) || isDeclinedEdit);

    if (requiresMerchantResubmitConfirm) {
      setShowResubmitConfirm(true);
      return;
    }

    await submitOffer();
  };

  useEffect(() => {
    let timer;
    if (showCelebration) {
      timer = setTimeout(() => {
        setShowCelebration(false);
      }, 5000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showCelebration]);

  const handleCloseCelebration = () => {
    setShowCelebration(false);
    setShowSuccessModal(false);
    navigate(role === "admin" ? "/admin/offer-list" : "/merchant/offer-list");
  };

  // Register the Next (now Submit) action
  useEffect(() => {
    registerNextAction(() => handleSubmit());
  }, [
    registerNextAction,
    offerData,
    editOfferData,
    selectedMerchant,
    isAdminRole,
    isMerchantRole,
    isFirstOffer,
    isLive,
    isLiveEdit,
    isDeclinedEdit,
    liveSaveFlags.preserveLiveOffer,
  ]); // Dependencies crucial for handleSubmit closure

  // Sync loading state
  useEffect(() => {
    setIsNextLoading(Boolean(loading || isSubmittingReview));
  }, [loading, isSubmittingReview, setIsNextLoading]);

  return (
    <div className="w-full flex flex-col items-center justify-center mt-28 sm:mt-20 pb-20">
      <div className="w-full max-w-[1100px] relative flex flex-col items-center">
        <div className="w-[90%] sm:w-[100%] md:w-[95%] lg:w-[90%] xl:w-[85%] offerform">
          <h2 className="text-[26px] sm:text-[32px] font-[600] text-[#00ADEF] font-poppins text-center mb-4 sm:mb-1">
            Review and Save <span className="text-[#303030]">Your Offer</span>
          </h2>
          <p className="font-poppins font-[400] sm:text-[10px] md:text-[12px] text-[#767676] mb-10 text-center">
            You're almost done! Check your details and make sure everything is
            accurate.
          </p>
          <MobileViews
            offerData={offerData}
            merchantData={merchantData}
            selectedMerchant={selectedMerchant}
          />
        </div>
      </div>

      {/* Show confetti only for first offer - Portalled to body */}
      {showCelebration && createPortal(<Confetti />, document.body)}

      {shouldShowProcessingModal &&
        createPortal(
          <div className="offer-processing-backdrop">
            <div className="offer-processing-card">
              <h3>Saving Offer...</h3>
              <p>Please wait while we save your changes.</p>
            </div>
          </div>,
          document.body,
        )}

      {showResubmitConfirm &&
        createPortal(
          <GenericPopup
            icon={InfoIcon}
            content="Your updated offer will be sent to admin for approval. Do you want to resubmit it now?"
            popupClassName="w-[335px] sm:w-[470px] pb-8"
            cancelBtn={
              <PrimaryOutlineButton
                label="Cancel"
                className="px-6 font-poppins text-[13px] md:text-[14px] text-center font-[500] h-[40px] md:h-[42px] rounded-[8px] mt-6 transition-all hover:bg-[#F0FBFF]"
                disabled={isConfirmingResubmit}
                onClick={() => setShowResubmitConfirm(false)}
              />
            }
            continueBtn={
              <PrimaryButton
                label={isConfirmingResubmit ? "Processing..." : "Resubmit"}
                loading={isConfirmingResubmit}
                className="px-6 font-poppins text-[13px] md:text-[14px] text-center font-[500] h-[40px] md:h-[42px] rounded-[8px] mt-6 transition-all shadow-sm bg-[#00ADEF] hover:opacity-90 text-white"
                onClick={async () => {
                  setIsConfirmingResubmit(true);
                  try {
                    await submitOffer({
                      closeResubmitConfirm: true,
                      forceResubmit: true,
                    });
                  } finally {
                    setIsConfirmingResubmit(false);
                  }
                }}
              />
            }
          />,
          document.body,
        )}

      {/* Always show success modal for all offers - Portalled to body to cover Sidebar */}
      {showSuccessModal &&
        createPortal(
          <div
            className="offer-success-modal-backdrop"
            onClick={handleCloseCelebration}
          >
            <div
              className="offer-success-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Offer Submitted!</h3>
              <p className="offer-success-message">
                {successMessage}
              </p>
              <div className="offer-success-actions">
                <PrimaryButton
                  className="offer-success-ok-btn w-[60%] h-[45px] rounded-[8px] text-[16px] font-[500] font-montserrat"
                  label="OK"
                  onClick={handleCloseCelebration}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default OfferReview;
